import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';


import { Observable } from 'rxjs';
import {
  AssetMeterPrice,
  AssetMeterPriceStatus,
} from 'src/app/models/assetMeterPrice.model';
import { BPSAdminLevel } from 'src/app/models/user.model';
import { AssetMeterPriceService } from 'src/app/services/asset-meter-price.service';
import { DtMeterTypeService } from 'src/app/services/dt-meter-type.service';
import { MeterModelService } from 'src/app/services/meter-model.service';
import { MeterService } from 'src/app/services/meter.service';
import { OrganizationTeamApiService } from 'src/app/services/organization-team-api.service';
import { TranslationUtils } from 'src/assets/i18n/translation.utils';

@Component({
  selector: 'crud-asset-meter-price',
  templateUrl: './crud-asset-meter-price.component.html',
  styleUrls: ['./crud-asset-meter-price.component.scss'],
})
export class CrudAssetMeterPriceComponent implements OnInit {
  @Input() searchText!: string;
  @Input() utilityId: number = 0;
  @Input() bpsAdminLevel!: BPSAdminLevel;

  @ViewChild('closeAddEditModal') closeAddEditModal!: ElementRef;
  @ViewChild('closeDeleteModal') closeDeleteModal!: ElementRef;
  @ViewChild('openEditModalBtn') openEditModalBtn!: ElementRef;

  public currentPage: number = 1;
  public pageSize: number = 10;
  public count: number = 0;

  assetMeterPrices: AssetMeterPrice[] = [];
  selectedItem: AssetMeterPrice = new AssetMeterPrice();
  isEditMode: boolean = false;
  actionLabel$!: Observable<string>;
  currency: string = '';

  vendors: { id: number; name: string }[] = [];
  meterMakes: { id: number; name: string }[] = [];
  meterModels: { id: number; name: string; meterBrandId?: number }[] = [];
  meterModelOptions: { id: number; name: string; meterBrandId?: number }[] = [];
  assetMeterTypes: { id: number; name: string }[] = [];

  statuses = [
    { id: AssetMeterPriceStatus.Active, name: 'Active' },
    { id: AssetMeterPriceStatus.Inactive, name: 'Inactive' },
  ];


  meterPriceDisplay: string = '';

  filterVendorId?: number;
  filterMeterMakeId?: number;
  filterAssetMeterTypeId?: number;
  filterStatus?: AssetMeterPriceStatus;

  constructor(
    private assetMeterPriceService: AssetMeterPriceService,
    private organizationService: OrganizationTeamApiService,
    private meterService: MeterService,
    private meterModelService: MeterModelService,
    private dtMeterTypeService: DtMeterTypeService,
    private toastr: ToastrService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.currency = localStorage.getItem('currency') || '';
    this.getVendors();
    this.getMeterMakes();
    this.getMeterModels();
    this.getAssetMeterTypes();

    if (this.utilityId != 0) {
      this.reloadTable();
    }

    this.updateActionLabel();
  }

  private updateActionLabel(): void {
    const key = this.isEditMode
      ? TranslationUtils.EDIT_KEY
      : TranslationUtils.NEW_KEY;
    this.actionLabel$ = this.translateService.stream(key);
  }

  reloadTable(page: any = null): void {
    if (page) {
      this.currentPage = page;
    }
    this.getAllAssetMeterPrices();
  }

  filter(): void {
    this.currentPage = 1;
    this.getAllAssetMeterPrices();
  }

  get hasActiveFilter(): boolean {
    return this.filterVendorId != null
      || this.filterMeterMakeId != null
      || this.filterAssetMeterTypeId != null
      || this.filterStatus != null;
  }

  clearFilters(): void {
    this.filterVendorId = undefined;
    this.filterMeterMakeId = undefined;
    this.filterAssetMeterTypeId = undefined;
    this.filterStatus = undefined;
    this.filter();
  }

  pageChange(value: any): void {
    this.currentPage = value;
    this.getAllAssetMeterPrices();
  }

  addNew(): void {
    this.isEditMode = false;
    this.selectedItem = new AssetMeterPrice();
    this.meterModelOptions = [];
    this.setMeterPriceDisplay();
    this.updateActionLabel();
  }

  openEditModal(item: AssetMeterPrice): void {
    this.selectItem(item);
    this.openEditModalBtn.nativeElement.click();
  }

  selectItem(item: AssetMeterPrice): void {
    this.isEditMode = true;
    this.selectedItem = { ...item };
    this.setMeterModelOptions();
    this.setMeterPriceDisplay();
    this.updateActionLabel();
  }

  onDelete(item: AssetMeterPrice): void {
    this.selectedItem = { ...item };
  }

  onMeterPriceInput(event: any): void {
    const input = event.target as HTMLInputElement;
    const caret = input.selectionStart ?? input.value.length;
    const digitsBeforeCaret = (input.value.slice(0, caret).match(/[0-9]/g) ?? []).length;

    const formatted = this.formatMeterPrice(input.value);
    this.meterPriceDisplay = formatted;
    input.value = formatted;

    let seen = 0;
    let position = digitsBeforeCaret === 0 ? 0 : formatted.length;
    for (let i = 0; i < formatted.length && digitsBeforeCaret > 0; i++) {
      if (/[0-9]/.test(formatted[i])) {
        seen++;
        if (seen === digitsBeforeCaret) {
          position = i + 1;
          break;
        }
      }
    }
    input.setSelectionRange(position, position);

    const parsed = parseFloat(formatted.replace(/,/g, ''));
    this.selectedItem.meterPrice = isNaN(parsed) ? undefined : parsed;
  }

  private formatMeterPrice(value: string): string {
    let cleaned = (value ?? '').replace(/[^0-9.]/g, '');

    const firstDot = cleaned.indexOf('.');
    if (firstDot !== -1) {
      cleaned = cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '');
    }

    const [whole, decimals] = cleaned.split('.');
    const grouped = whole ? Number(whole).toLocaleString('en-US') : '';

    return decimals === undefined ? grouped : grouped + '.' + decimals.slice(0, 2);
  }

  private setMeterPriceDisplay(): void {
    this.meterPriceDisplay = this.selectedItem.meterPrice != null
      ? this.selectedItem.meterPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : '';
  }

  onMeterMakeChange(): void {
    this.selectedItem.meterModelId = undefined;
    this.setMeterModelOptions();
  }

  private setMeterModelOptions(): void {
    this.meterModelOptions = this.selectedItem.meterBrandId
      ? this.meterModels.filter((x) => x.meterBrandId === this.selectedItem.meterBrandId)
      : [];
  }

  onVendorSelected(id: number): void {
    this.selectedItem.organizationId = id || undefined;
  }

  onMeterMakeSelected(id: number): void {
    this.selectedItem.meterBrandId = id || undefined;
    this.onMeterMakeChange();
  }

  onModalFade(): void {
    this.isEditMode = false;
    this.selectedItem = new AssetMeterPrice();
    this.meterModelOptions = [];
    this.setMeterPriceDisplay();
  }

  getVendorName(id?: number): string {
    return this.vendors.find((x) => x.id === id)?.name ?? '';
  }

  getMeterMakeName(id?: number): string {
    return this.meterMakes.find((x) => x.id === id)?.name ?? '';
  }

  getMeterModelName(id?: number): string {
    return this.meterModels.find((x) => x.id === id)?.name ?? '';
  }

  getAssetMeterTypeName(id?: number): string {
    return this.assetMeterTypes.find((x) => x.id === id)?.name ?? '';
  }

  getStatusName(status?: AssetMeterPriceStatus): string {
    return this.statuses.find((x) => x.id === status)?.name ?? '';
  }

  private toStatus(value: any): AssetMeterPriceStatus | undefined {
    if (value === null || value === undefined) {
      return undefined;
    }
    return typeof value === 'number'
      ? value
      : AssetMeterPriceStatus[value as keyof typeof AssetMeterPriceStatus];
  }

  customIdMessage(event: any): void {
    event.target.setCustomValidity(
      'Please enter an alphanumeric ID (letters and numbers only).'
    );
  }

  clearCustomMessage(event: any): void {
    event.target.setCustomValidity('');
  }

  save(): void {
    if (!this.selectedItem.organizationId) {
      this.toastr.error('Please select a vendor.');
      return;
    }

    if (!this.selectedItem.meterBrandId) {
      this.toastr.error('Please select a meter make.');
      return;
    }

    if (this.selectedItem.status === undefined || this.selectedItem.status === null) {
      this.toastr.error('Please select a status.');
      return;
    }

    if (this.selectedItem.meterPrice == null || this.selectedItem.meterPrice <= 0) {
      this.toastr.error('Please enter a meter price greater than zero.');
      return;
    }

    this.selectedItem.utilityId = this.utilityId;

    this.assetMeterPriceService.save(this.selectedItem).subscribe({
      next: (res) => {
        const response = res?.body;
        if (response?.status === 200 || response?.status === 'OK') {
          this.closeAddEditModal?.nativeElement?.click();
          this.toastr.success(response.message);
          this.reloadTable(this.isEditMode ? null : 1);
        } else {
          this.toastr.error(response?.message);
        }
      },
      error: () => this.toastr.error('An error occurred.'),
    });
  }

  deleteItem(): void {
    this.assetMeterPriceService.delete(this.selectedItem.id).subscribe({
      next: (res) => {
        const response = res?.body;
        this.closeDeleteModal?.nativeElement?.click();
        if (response?.status === 200 || response?.status === 'OK') {
          this.toastr.success(response.message);
          this.reloadTable(1);
        } else {
          this.toastr.error(response?.message);
        }
      },
      error: () => this.toastr.error('An error occurred.'),
    });
  }

  private getAllAssetMeterPrices(): void {
    const dataIn = {
      pageInfo: {
        page: this.currentPage,
        pageSize: this.pageSize,
      },
      filterParams: {
        searchValue: this.searchText,
        organizationId: this.filterVendorId ?? null,
        meterBrandId: this.filterMeterMakeId ?? null,
        assetMeterTypeId: this.filterAssetMeterTypeId ?? null,
        status: this.filterStatus ?? null,
      },
      utilityId: this.utilityId,
    };

    this.assetMeterPriceService.getAll(dataIn).subscribe({
      next: (res) => {
        const response = res?.body;
        if (response?.status === 200 || response?.status === 'OK') {
          this.assetMeterPrices = (response.data.data ?? []).map((x: any) => ({
            ...x,
            status: this.toStatus(x.status),
          }));
          this.count = response.data.count;
          if (this.count === 0) {
            this.toastr.info('No data for given input.');
          }
        } else {
          this.toastr.error(response?.message);
        }
      },
      error: () => this.toastr.error('An error occurred.'),
    });
  }

  private getVendors(): void {
    this.organizationService.getAllForSelect().subscribe({
      next: (response: any) => {
        if (response?.status === 200 || response?.status === 'OK') {
          this.vendors = (response.data ?? []).map((x: any) => ({
            id: x.id,
            name: x.name,
          })).sort((a: any, b: any) => a.name.localeCompare(b.name));

        }
      },
      error: () =>
        this.toastr.error('An error occurred while fetching vendors.'),
    });
  }

  private getMeterMakes(): void {
    this.meterService.getAllMeterTypesForSelect().subscribe({
      next: (res: any) => {
        const response = res?.body;
        if (response?.status === 200 || response?.status === 'OK') {
          this.meterMakes = (response.data ?? []).map((x: any) => ({
            id: x.id,
            name: x.name,
          })).sort((a: any, b: any) => a.name.localeCompare(b.name));

        }
      },
      error: () =>
        this.toastr.error('An error occurred while fetching meter makes.'),
    });
  }

  private getMeterModels(): void {
    this.meterModelService.getAllForSelect().subscribe({
      next: (res: any) => {
        const response = res?.body;
        if (response?.status === 200 || response?.status === 'OK') {
          this.meterModels = (response.data ?? []).map((x: any) => ({
            id: x.id,
            name: x.name,
            meterBrandId: x.meterBrand?.id,
          }));
        }
      },
      error: () =>
        this.toastr.error('An error occurred while fetching meter models.'),
    });
  }

  private getAssetMeterTypes(): void {
    this.dtMeterTypeService.getAllForSelect().subscribe({
      next: (res: any) => {
        const response = res?.body;
        if (response?.status === 200 || response?.status === 'OK') {
          this.assetMeterTypes = (response.data ?? []).map((x: any) => ({
            id: x.id,
            name: x.type,
          }));

        }
      },
      error: () =>
        this.toastr.error('An error occurred while fetching asset meter types.'),
    });
  }
}
